/**
 * The seam between the idle screens and the server (D240, D241).
 *
 * The client runs the sim itself and draws the result at once (optimistic), then hands the
 * same intent to `idleSync`, whose answer replaces it. Everything that crosses the wire goes
 * through `IdleSync` and nothing else:
 *
 *   open()              GET /world?open=1: the world, checked in, with the welcome back, or
 *                       that there is no world yet (D266: the first run names one)
 *   create(name, seed, theme)  POST /world: a new idle world under the name, seed and theme the player chose (D266, D267, D359)
 *   deleteWorld(id)      POST /worlds/:id/delete: any of the account's worlds, once its name is typed (D221, D267, D337)
 *   selectWorld(id)      POST /worlds/:id/select: make another of the account's idle worlds the open one (D337)
 *   send(intent, at)    POST /world/act: one intent; the server's world replaces the local one
 *   listWorlds()         GET /worlds: the account's worlds, the archived classic ones marked
 *   region(id)          GET /region/:id: the weekly goal and the table of worlds
 *   viewWorld(id)        GET /worlds/:id/view: an archived world as it was left, to look at (D262)
 *
 * `serverIdleSync` is the app's. `localIdleSync` runs the same sim against localStorage and
 * is kept for tests only.
 */
import { getIdleContent } from '@hedgerow/data';
import {
  applyIdleIntent,
  checkInIdle,
  createIdleWorld,
  idleWelcomeBack,
  type IdleWorldState,
  type IdleIntent,
  type IdleWelcomeBack,
  type IdleActResponse,
  type IdleRegionPublic,
  type IdleRegionView,
  type IdleWorldResponse,
  type RenameWorldResponse,
  type WorldViewResponse,
} from '@hedgerow/sim';
import { now, setFakeNow } from '../clock.ts';
import { deleteWorldFailed } from '../game/copy.ts';
import {
  apiRequest,
  deleteWorld as deleteWorldCall,
  ensureSession,
  fetchWorlds,
  uuid,
} from '../game/sync.ts';

/** One of the account's worlds, for the world list in settings. */
export interface IdleWorldEntry {
  worldId: string;
  name: string;
  /** The one this device is playing. */
  current: boolean;
  /** A classic world from before the idle game (D241): listed, looked at, never played. */
  archived: boolean;
  /** An archived world's stage and world value, from the list (D262). */
  stage?: number;
  worldValue?: number;
  /** D267: the seed that shaped the land, to share. */
  mapSeed?: string;
  /** D344: the world's kind and land traits by id, its kind first. */
  mapTraits?: string[];
  /** D337: an idle world's Heritage, named when it is deleted. */
  heritage?: number;
  /** D348: the world's theme, by id. Absent on an archived classic world, which has none. */
  theme?: string;
  /** D375: the year the world is in, when the list carries it (the chooser shows it then). */
  year?: number;
  /**
   * D439: what the open world is doing now, which the list does not carry: the year and
   * generation in the theme's words ("Year 5 · Generation 2"), its coins and its income.
   * Set on the current world's row from the store, never stored.
   */
  yearLine?: string;
  money?: number;
  rate?: number;
}

/** An archived world as it was left, for the read-only summary (D262). */
export interface ArchivedWorldView {
  name: string;
  stage: number;
  /** When the player was last on it, or null when the save does not say. */
  lastPlayedAt: number | null;
}

export interface IdleOpened {
  world: IdleWorldState;
  /** D312: the world's own name, shown in the header and settings. */
  name: string;
  /** What the barn banked while the player was away, from before the check-in. */
  welcome?: IdleWelcomeBack;
  region?: IdleRegionView;
  /** D336: the account's world id, to key screen state kept per world (the Heritage dismissal). */
  worldId?: string;
  /** D348: the world's theme, by id. Absent means the farm theme. */
  theme?: string;
}

/**
 * D266: there is nothing to open. `world`: the account has no world, so the first run names
 * one. `sign_in`: this device has signed in before and its session has ended (D190), so it
 * is not given a guest world without asking.
 */
export interface IdleNoWorld {
  needs: 'world' | 'sign_in';
}

/**
 * What `create` came to: the world is made, a session is needed first, nothing answered, or the
 * server refused it with words to show (D337: the world limit, a name already in use).
 */
export type IdleCreateResult = 'ok' | 'sign_in' | 'failed' | { reason: string };

/** What the server says to an intent. `world` is the authoritative state when it answers. */
export type IdleSendResult =
  | { ok: true; world: IdleWorldState }
  | { ok: false; reason: string; needs?: number; world?: IdleWorldState }
  /** Nothing answered: keep the optimistic state. */
  | { ok: null };

export interface IdleSync {
  /** Open the world and check the player in. Also the heartbeat while the app is visible. */
  open(): Promise<IdleOpened | IdleNoWorld | null>;
  /** Make the account's new idle world under `name` (D266). The caller opens it after. */
  create(name: string, mapSeed?: string, theme?: string): Promise<IdleCreateResult>;
  /** D267, D337: delete one of the account's worlds. Null when it went; otherwise the reason to show. */
  deleteWorld(worldId: string): Promise<string | null>;
  /** D337: open another of the account's idle worlds. Null when it worked; otherwise the reason. */
  selectWorld(worldId: string): Promise<string | null>;
  /** D312: rename the current world. Null when it worked; otherwise the reason to show. */
  renameWorld(name: string, worldId?: string): Promise<string | null>;
  send(intent: IdleIntent, at: number): Promise<IdleSendResult>;
  /** The account's worlds, or null when the server did not answer. */
  listWorlds(): Promise<IdleWorldEntry[] | null>;
  region(regionId: string): Promise<IdleRegionPublic | null>;
  viewWorld(worldId: string): Promise<ArchivedWorldView | null>;
}

// ---------------------------------------------------------------------------------------
// The server.

/** The stub's world name, for tests. A real world is named in the first run (D266). */
export const DEFAULT_WORLD_NAME = 'Home Farm';

/** D337, D363: shown when a switch could not be told to the server at all. */
const SELECT_WORLD_FAILED = 'Could not open that world. Try again.';

/** D312, D363: shown when a rename could not be told to the server at all. */
const RENAME_WORLD_FAILED = 'Could not rename the world. Try again.';

let aligned = false;

/**
 * The server's clock wins: the sim on this device runs on the time the server reports. The
 * first answer sets the clock either way; after that it only moves forward, because an
 * answer is already old when it lands, and one that set off before the clock moved on
 * would otherwise pull it back and drop a bonus that has just begun.
 */
function alignClock(serverTime: number | undefined): void {
  if (typeof serverTime !== 'number' || !Number.isFinite(serverTime)) return;
  if (!aligned || serverTime > now()) setFakeNow(serverTime);
  aligned = true;
}

async function json<T>(res: Response | null): Promise<T | null> {
  if (!res) return null;
  try {
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

let currentWorldId: string | null = null;
let sessionChecked = false;
let needsSignIn = false;

async function getWorld(): Promise<Response | null> {
  return apiRequest('/world?open=1', { cache: 'no-store' });
}

async function postWorld(name: string, mapSeed?: string, theme?: string): Promise<Response | null> {
  return apiRequest('/world', {
    method: 'POST',
    body: JSON.stringify({ name, ...(mapSeed ? { mapSeed } : {}), ...(theme ? { theme } : {}) }),
  });
}

export const serverIdleSync: IdleSync = {
  async open() {
    if (!sessionChecked) {
      const s = await ensureSession();
      if (s.kind === 'unreachable') return null;
      needsSignIn = s.kind === 'needs-sign-in';
      sessionChecked = true;
    }
    const res = await getWorld();
    // D266: no world yet. The first run names it and `create` makes it.
    if (res?.status === 404) return { needs: 'world' };
    if (res?.status === 401) return { needs: needsSignIn ? 'sign_in' : 'world' };
    if (!res?.ok) return null;
    const body = await json<IdleWorldResponse>(res);
    if (!body?.ok || body.kind !== 'idle') return null;
    alignClock(body.serverTime);
    currentWorldId = body.worldId;
    return {
      world: body.state,
      name: body.name,
      region: body.region,
      worldId: body.worldId,
      theme: body.theme,
      ...(body.welcomeBack ? { welcome: body.welcomeBack } : {}),
    };
  },

  async create(name, mapSeed, theme) {
    let res = await postWorld(name, mapSeed, theme);
    if (res?.status === 401) {
      // The session has gone. A device that never signed in is given a guest again; one
      // that has signed in before is asked to sign in (D190).
      const s = await ensureSession();
      if (s.kind === 'needs-sign-in') return 'sign_in';
      if (s.kind === 'unreachable') return 'failed';
      res = await postWorld(name, mapSeed, theme);
    }
    if (res?.ok) return 'ok';
    // D337: the world limit or a name in use, said in the server's words.
    if (res?.status === 409) {
      const body = await json<{ reason?: string }>(res);
      if (body?.reason) return { reason: body.reason };
    }
    return 'failed';
  },

  async send(intent, at) {
    const res = await apiRequest('/world/act', {
      method: 'POST',
      body: JSON.stringify({ id: uuid(), clientTime: Math.round(at), intents: [intent] }),
    });
    if (!res) return { ok: null };
    const body = await json<IdleActResponse | { ok: false; error?: string; reason?: string }>(res);
    if (!body) return { ok: null };
    if (!body.ok) return { ok: false, reason: body.reason ?? body.error ?? 'refused' };
    alignClock(body.serverTime);
    const r = body.results[0];
    if (r && !r.ok)
      return {
        ok: false,
        reason: r.reason,
        ...(r.needs ? { needs: r.needs } : {}),
        world: body.state,
      };
    return { ok: true, world: body.state };
  },

  async listWorlds() {
    const listed = await fetchWorlds().catch(() => null);
    if (!listed) return null;
    return listed.worlds.map((f) => ({
      worldId: f.worldId,
      name: f.name,
      current: f.worldId === (currentWorldId ?? listed.currentWorldId),
      archived: f.archived,
      ...(f.archived ? { stage: f.stage, worldValue: f.worldValue } : {}),
      ...(f.mapSeed ? { mapSeed: f.mapSeed } : {}),
      ...(f.mapTraits?.length ? { mapTraits: f.mapTraits } : {}),
      ...(!f.archived && f.heritage ? { heritage: f.heritage } : {}),
      ...(!f.archived ? { theme: f.theme } : {}),
    }));
  },

  async deleteWorld(worldId) {
    const r = await deleteWorldCall(worldId);
    if (!r) return deleteWorldFailed();
    if (r.ok) {
      // D337: the open world went, so the server has moved the session on (or to none).
      if (worldId === currentWorldId) currentWorldId = r.currentWorldId || null;
      return null;
    }
    return ('reason' in r && r.reason) || deleteWorldFailed();
  },

  async selectWorld(worldId) {
    const res = await apiRequest(`/worlds/${encodeURIComponent(worldId)}/select`, {
      method: 'POST',
    });
    if (res?.ok) {
      currentWorldId = worldId;
      return null;
    }
    const body = await json<{ reason?: string }>(res);
    return body?.reason ?? SELECT_WORLD_FAILED;
  },

  async renameWorld(name, worldId) {
    const id = worldId ?? currentWorldId;
    if (!id) return RENAME_WORLD_FAILED;
    const res = await apiRequest(`/worlds/${encodeURIComponent(id)}/rename`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    const body = await json<RenameWorldResponse>(res);
    if (!body) return RENAME_WORLD_FAILED;
    if (body.ok) return null;
    return body.reason ?? RENAME_WORLD_FAILED;
  },

  async viewWorld(worldId) {
    const res = await apiRequest(`/worlds/${encodeURIComponent(worldId)}/view`, {
      cache: 'no-store',
    });
    if (!res?.ok) return null;
    const body = await json<WorldViewResponse>(res);
    if (!body?.ok) return null;
    if (body.kind === 'idle')
      return { name: body.name, stage: 1, lastPlayedAt: body.state.lastSeen };
    return {
      name: body.name,
      stage: body.state.stage,
      lastPlayedAt: typeof body.state.lastSeenAt === 'number' ? body.state.lastSeenAt : null,
    };
  },

  async region(regionId) {
    const res = await apiRequest(`/region/${encodeURIComponent(regionId)}`, {
      cache: 'no-store',
    });
    if (!res?.ok) return null;
    const body = await json<{ ok: true; region: IdleRegionPublic }>(res);
    return body?.ok ? body.region : null;
  },
};

// ---------------------------------------------------------------------------------------
// The stub, for tests: the same sim, kept in localStorage.

export const LOCAL_WORLD_KEY = 'hedgerow-idle-world';

function load(): IdleWorldState | null {
  try {
    const raw = globalThis.localStorage?.getItem(LOCAL_WORLD_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as IdleWorldState;
    return s && s.version === 1 ? s : null;
  } catch {
    return null;
  }
}

function save(s: IdleWorldState): void {
  try {
    globalThis.localStorage?.setItem(LOCAL_WORLD_KEY, JSON.stringify(s));
  } catch {
    // A private window: the world lasts as long as the tab.
  }
}

/** A stub with the server's shape, for tests. `clock` is its time. */
export function localIdleSync(clock: () => number): IdleSync {
  let held: IdleWorldState | null = null;
  return {
    async open() {
      const t = clock();
      const before = held ?? load() ?? createIdleWorld(getIdleContent(), t);
      const welcome = idleWelcomeBack(before, getIdleContent(), t);
      held = checkInIdle(before, getIdleContent(), t);
      save(held);
      return { world: held, name: DEFAULT_WORLD_NAME, welcome, worldId: 'local' };
    },
    async create() {
      return 'ok';
    },
    async deleteWorld() {
      return null;
    },
    async selectWorld() {
      return null;
    },
    async renameWorld() {
      return null;
    },
    async send(intent, at) {
      const base = held ?? load() ?? createIdleWorld(getIdleContent(), at);
      const r = applyIdleIntent(base, getIdleContent(), intent, at);
      if (!r.ok) return { ok: false, reason: r.reason, ...(r.needs ? { needs: r.needs } : {}) };
      held = r.state;
      save(held);
      return { ok: true, world: held };
    },
    async listWorlds() {
      return [{ worldId: 'local', name: DEFAULT_WORLD_NAME, current: true, archived: false }];
    },
    async region() {
      return null;
    },
    async viewWorld() {
      return null;
    },
  };
}

/** The sync the app uses. */
export const idleSync: IdleSync = serverIdleSync;
